import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { v4 } from 'uuid'

import AddIcon from '@mui/icons-material/AddRounded'

import { LocalFile, RemoteFile, Zone, ZoneType } from '@types'

import { useModal } from '@hooks/app/modal.hook'

import { useUpdateZone } from '@hooks/zone/update-zone.hook'

import { Modal } from '@components/Modal'
import { Refresh } from '@components/Refresh'

import { useZone } from '@hooks/zone/zone.hook'
import { useCreateZone } from '@hooks/zone/create-zone.hook'
import { useDeleteZone } from '@hooks/zone/delete-zone.hook'

import { ZoneCard } from '../../components/ZoneCard'

import { AddZoneButton, AddZoneIcon, AddZoneLabel, Container } from './manage-zones.styled'
import { useCallback, useEffect, useState } from 'react'
import { getFileUrl } from '@utils/getFileUrl'

type ExtandedZone = Zone & {
  status: 'default' | 'created' | 'updated' | 'deleted'
}

type ExtandedZoneWithFile = { zone: ExtandedZone; files: (LocalFile | RemoteFile)[] }

export const ManageZonesPage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const { createZone } = useCreateZone()
  const { updateZone } = useUpdateZone()
  const { deleteZone } = useDeleteZone()

  const [searchParams] = useSearchParams()

  const { projectId } = useParams()
  const floorId = searchParams.get('floorId')

  const { data, loading, error } = useZone(floorId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const [zones, setZones] = useState<ExtandedZoneWithFile[]>([])

  const loadPlanPreview = async (zoneData: Zone): Promise<RemoteFile[]> => {
    if (zoneData.plans && zoneData.plans[0]?.url) {
      const plan = zoneData.plans[0]
      const url = await getFileUrl(plan.url)

      return [
        {
          id: plan.id,
          deleted: false,
          name: plan.file.name,
          type: plan.file.type,
          extension: plan.file.extension,
          url,
        },
      ]
    } else {
      return []
    }
  }

  const loadFilesFromZones = useCallback(async () => {
    if (data?.zone?.children) {
      const parent = data.zone
      const mappedZones: ExtandedZoneWithFile[] = await Promise.all(
        data.zone.children.map(async (z) => {
          const files = await loadPlanPreview(z)

          return {
            zone: {
              ...z,
              status: 'default',
            },
            files: files,
          }
        }),
      )

      const defaultZones = mappedZones.length == 0 ? [getNewZone({ parentId: parent.id })] : mappedZones

      setZones(defaultZones)
    }
  }, [data?.zone])

  useEffect(() => {
    loadFilesFromZones()
  }, [loadFilesFromZones])

  const sortedZones = zones.sort((a, b) => (a.zone.index < b.zone.index ? -1 : a.zone.index == b.zone.index ? 0 : 1))
  const filteredZones = sortedZones.filter((z) => z.zone.status != 'deleted')

  const create = async (name: string) => {
    const newZone = getNewZone({
      name,
      parentId: floorId || undefined,
      index: getLastIndexFromZones(filteredZones),
    })

    setZones([...zones, newZone])
  }

  const duplicateZone = async (zoneWithFile: ExtandedZoneWithFile) => {
    const newZone: ExtandedZoneWithFile = getNewZone({
      name: `Zone ${zones.length + 1}`,
      description: zoneWithFile.zone.description,
      parentId: zoneWithFile.zone.parentId,
      index: getLastIndexFromZones(filteredZones),
      files: zoneWithFile.files,
    })

    setZones([...zones, newZone])
  }

  const update = (zoneWithFile: ExtandedZoneWithFile) => {
    const mappedZones: ExtandedZoneWithFile[] = zones.map((z) => {
      if (z.zone.id == zoneWithFile.zone.id) {
        return {
          zone: {
            ...zoneWithFile.zone,
            status: zoneWithFile.zone.status == 'default' ? 'updated' : zoneWithFile.zone.status,
          },
          files: zoneWithFile.files,
        }
      }

      return z
    })
    setZones(mappedZones)
  }

  const remove = (zoneWithFile: ExtandedZoneWithFile) => {
    if (zoneWithFile.zone.status == 'created') {
      const filteredZones = zones.filter((z) => z.zone.id != zoneWithFile.zone.id)
      setZones(filteredZones)
    } else {
      const mappedZones = zones.map((z) =>
        z.zone.id == zoneWithFile.zone.id
          ? ({ zone: { ...zoneWithFile.zone, status: 'deleted' }, files: zoneWithFile.files } as ExtandedZoneWithFile)
          : z,
      )
      setZones(mappedZones)
    }
  }

  const save = () => {
    const deletedZones = sortedZones.filter((z) => z.zone.status == 'deleted')

    // Delete zone
    for (const zone of deletedZones) {
      deleteZone(zone.zone.id)
    }

    // Update zone
    const updatedZones = sortedZones.filter((z) => z.zone.status == 'updated')
    for (const zone of updatedZones) {
      if (projectId) {
        updateZone(
          {
            id: zone.zone.id,
            name: zone.zone.name,
            description: zone.zone.description,
            index: zone.zone.index,
          },
          zone.files,
          projectId,
        )
      }
    }

    // Create zone
    const createdZones = sortedZones.filter((z) => z.zone.status == 'created')
    for (const zone of createdZones) {
      if (projectId) {
        createZone(
          {
            projectId,
            parentId: zone.zone.parentId,
            name: zone.zone.name,
            description: zone.zone.description,
            type: ZoneType.Zone,
            index: zone.zone.index,
          },
          zone.files.filter((file): file is LocalFile => 'data' in file) as LocalFile[],
        )
      }
    }
  }

  return (
    <Modal
      title={t('modules_project-settings_pages_manage_zones_title')}
      onSubmitLabel={t('modules_project-settings_pages_manage_zones_button_save-zones')}
      onClose={() => {
        hideModal('manage_zones', { floorId })
      }}
      onSubmit={async () => {
        save()

        hideModal('manage_zones', { floorId })
      }}
    >
      <Container>
        <Refresh loading={isLoading} small>
          {filteredZones.length > 0 &&
            filteredZones.map((zoneWithFile) => (
              <ZoneCard
                key={zoneWithFile.zone.id}
                zone={zoneWithFile.zone}
                files={zoneWithFile.files}
                onUpdateZoneFile={(plans) => update({ zone: { ...zoneWithFile.zone }, files: [...plans] })}
                onUpdateZone={(name, description) =>
                  update({ zone: { ...zoneWithFile.zone, name, description }, files: zoneWithFile.files })
                }
                onDuplicateZone={() => duplicateZone(zoneWithFile)}
                onDeleteZone={() => {
                  remove(zoneWithFile)
                }}
              />
            ))}
        </Refresh>

        <div>
          <AddZoneButton
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()

              create('Zone ...')
            }}
          >
            <AddZoneIcon>
              <AddIcon />
            </AddZoneIcon>
            <AddZoneLabel>{t('modules_project-settings_pages_manage_zones_button_add-zone')}</AddZoneLabel>
          </AddZoneButton>
        </div>
      </Container>
    </Modal>
  )
}

const getLastIndexFromZones = (zones: ExtandedZoneWithFile[]): number => {
  const sortedZones = zones.sort((a, b) => (a.zone.index > b.zone.index ? -1 : a.zone.index == b.zone.index ? 0 : 1))
  const index = sortedZones[0] ? sortedZones[0].zone.index + 1 : 0
  return index
}

const getNewZone = (params: {
  name?: string
  description?: string
  parentId?: string
  index?: number
  files?: (LocalFile | RemoteFile)[]
}): ExtandedZoneWithFile => {
  const { name = 'Zone 1', description = '', parentId, index = 0 } = params

  return {
    zone: {
      id: v4(),
      createdDate: new Date(),
      updatedDate: new Date(),
      name,
      description,
      type: ZoneType.Zone,
      parentId,
      index,
      children: [],
      status: 'created',
      plans: [],
    },
    files: [],
  }
}
